/**
 * Canvas-equivalent of the `homeSafeFormatting` mixin.
 *
 * `homeSafeFormatting` turns "HomeSafe®" into "Home<em>Safe</em><sup>®</sup>" for
 * HTML contexts (rendered via v-html). Fabric.js text objects render plain
 * characters and cannot interpret HTML, so on the canvas the same wordmark is
 * expressed through per-character `styles` instead: "Safe" italic, "®"
 * superscript (raised, smaller, never italic), everything else upright.
 *
 * Styles are derived from the string at runtime (the "Safe"/"®" positions are
 * located, not hardcoded) so the result survives copy edits and localization.
 *
 * @see ENG-11456
 */

const WORDMARK_PREFIX = 'Home';
const EMPHASIZED_WORD = 'Safe';
const WORDMARK = WORDMARK_PREFIX + EMPHASIZED_WORD; // "HomeSafe"
const REGISTERED_SYMBOL = '®';

// Superscript tuning for the ® glyph, relative to the line's base font size.
const SUPERSCRIPT_FONT_RATIO = 0.6;
const SUPERSCRIPT_DELTA_Y_RATIO = -0.35;

/**
 * Resolve the base style for a given line index.
 *
 * @param {object|function} basePerLine A single base style object applied to
 *   every line, or a `(lineIndex) => style` callback for per-line bases.
 * @param {number} lineIndex
 * @returns {object}
 */
function resolveBaseStyle(basePerLine, lineIndex) {
  const style = typeof basePerLine === 'function' ? basePerLine(lineIndex) : basePerLine;
  return style ? { ...style } : {};
}

/**
 * Build a Fabric `styles` map for a (possibly multi-line) label that contains
 * the HomeSafe wordmark.
 *
 * @param {string} text The label text, lines separated by "\n".
 * @param {object|function} basePerLine Base style for all lines, or a
 *   `(lineIndex) => style` callback.
 * @returns {object} Fabric styles keyed as `{ [lineIndex]: { [charIndex]: style } }`.
 */
export function buildHomeSafeCanvasStyles(text, basePerLine = {}) {
  if (!text) {
    return {};
  }

  const styles = {};

  text.split('\n').forEach((line, lineIndex) => {
    const baseStyle = resolveBaseStyle(basePerLine, lineIndex);
    const lineStyles = {};

    // Apply the base style to every character on the line.
    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      lineStyles[charIndex] = { ...baseStyle };
    }

    // Style each "HomeSafe" wordmark occurrence: italicize its "Safe" segment,
    // and superscript an immediately-trailing "®". Anchoring on the full
    // wordmark avoids touching unrelated text (e.g. "Safety", a standalone
    // "Safe", or a "®" belonging to some other mark).
    const baseFontSize = typeof baseStyle.fontSize === 'number' ? baseStyle.fontSize : undefined;
    let searchFrom = 0;
    let wordmarkIndex = line.indexOf(WORDMARK, searchFrom);
    while (wordmarkIndex !== -1) {
      // Italicize the "Safe" segment of "HomeSafe".
      const safeIndex = wordmarkIndex + WORDMARK_PREFIX.length;
      for (let offset = 0; offset < EMPHASIZED_WORD.length; offset++) {
        lineStyles[safeIndex + offset] = { ...baseStyle, fontStyle: 'italic' };
      }

      // Superscript the "®" only when it immediately follows the wordmark —
      // raised, smaller, and explicitly upright so it never inherits the
      // adjacent italic run.
      const registeredIndex = wordmarkIndex + WORDMARK.length;
      if (line[registeredIndex] === REGISTERED_SYMBOL) {
        lineStyles[registeredIndex] = {
          ...baseStyle,
          fontStyle: 'normal',
          ...(baseFontSize
            ? {
                fontSize: Math.round(baseFontSize * SUPERSCRIPT_FONT_RATIO),
                deltaY: Math.round(baseFontSize * SUPERSCRIPT_DELTA_Y_RATIO),
              }
            : {}),
        };
      }

      searchFrom = wordmarkIndex + WORDMARK.length;
      wordmarkIndex = line.indexOf(WORDMARK, searchFrom);
    }

    if (Object.keys(lineStyles).length > 0) {
      styles[lineIndex] = lineStyles;
    }
  });

  return styles;
}

export const homeSafeCanvasStyles = {
  methods: {
    buildHomeSafeCanvasStyles,
  },
};
